/**
 * Превью вложений на ленте: тип файла, MIME, байты поля `preview` из UniData.
 *
 * Не выполняет download с сервера — только разбор входящих данных и эвристики по имени.
 *
 * @see dev/WUI-293.md — п. 5–6, 12
 */
const RASTER_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.wbmp'] as const

/** Имя файла в нижнем регистре без пробелов по краям. */
export function fileNameLower(name: string): string {
  return name.trim().toLowerCase()
}

/** Растровое изображение по расширению (ориентир Swing / ImageIO). */
export function isRasterFileName(fileName: string): boolean {
  const n = fileNameLower(fileName)
  return RASTER_EXTENSIONS.some((ext) => n.endsWith(ext))
}

/** PDF по расширению `.pdf`. */
export function isPdfFileName(fileName: string): boolean {
  return fileNameLower(fileName).endsWith('.pdf')
}

const OFFICE_EXTENSIONS = [
  '.doc',
  '.docx',
  '.docm',
  '.odt',
  '.rtf',
  '.xls',
  '.xlsx',
  '.xlsm',
  '.ods',
  '.csv',
  '.ppt',
  '.pptx',
  '.pptm',
  '.odp',
] as const

/** Офисные форматы (Word/Excel/PowerPoint) — viewer и Р7 (WUI-305). */
export function isOfficeFileName(fileName: string): boolean {
  const n = fileNameLower(fileName)
  return OFFICE_EXTENSIONS.some((ext) => n.endsWith(ext))
}

/** MIME для `Blob` превью по расширению; по умолчанию `image/jpeg`. */
export function guessImageMimeFromFileName(fileName: string): string {
  const n = fileNameLower(fileName)
  if (n.endsWith('.png')) return 'image/png'
  if (n.endsWith('.gif')) return 'image/gif'
  if (n.endsWith('.bmp')) return 'image/bmp'
  if (n.endsWith('.webp')) return 'image/webp'
  return 'image/jpeg'
}

/**
 * Middleware отдаёт тело файла как `application/octet-stream`.
 * Для iframe/img нужен конкретный MIME, иначе браузер инициирует скачивание.
 */
export function coerceBlobMimeForPreview(blob: Blob, fileName: string): Blob {
  const current = blob.type?.toLowerCase() ?? ''
  if (current && current !== 'application/octet-stream') return blob
  if (isPdfFileName(fileName)) {
    return new Blob([blob], { type: 'application/pdf' })
  }
  if (isRasterFileName(fileName)) {
    return new Blob([blob], { type: guessImageMimeFromFileName(fileName) })
  }
  return blob
}

/** Декодирует base64 из поля `preview` (без переносов строк). */
export function decodeBase64ToBytes(data: string): Uint8Array {
  const normalized = String(data).replace(/\r?\n/g, '')
  return Uint8Array.from(atob(normalized), (c) => c.charCodeAt(0))
}

function unwrapTypedValue(field: unknown): unknown {
  if (field != null && typeof field === 'object' && 'value' in field) {
    return (field as { value: unknown }).value
  }
  return field
}

/** Байты миниатюры из поля `preview` строки (входящий fill / reloadElement). */
export function previewBytesFromFields(fields: Record<string, unknown> | undefined): Uint8Array | null {
  if (!fields?.preview) return null
  const raw = unwrapTypedValue(fields.preview)
  if (raw == null) return null
  if (raw instanceof Uint8Array) return raw
  if (raw instanceof ArrayBuffer) return new Uint8Array(raw)
  if (Array.isArray(raw)) return new Uint8Array(raw)
  if (typeof raw === 'string' && raw.length > 0) {
    try {
      return decodeBase64ToBytes(raw)
    } catch {
      return null
    }
  }
  return null
}

/** `Blob` для `URL.createObjectURL` из поля `preview` строки, если байты есть. */
export function blobFromPreviewField(
  fields: Record<string, unknown> | undefined,
  fileName: string,
): Blob | null {
  const bytes = previewBytesFromFields(fields)
  if (!bytes?.length) return null
  return new Blob([bytes], { type: guessImageMimeFromFileName(fileName) })
}

/** Режим отображения крупного превью в `MFileInput`. */
export type AttachmentPreviewKind = 'image' | 'pdf' | 'office' | 'placeholder' | 'none'

/** Выбирает режим превью по имени файла (растр / PDF / Office inline / заглушка). */
export function resolveAttachmentPreviewKind(fileName: string): AttachmentPreviewKind {
  if (!fileName) return 'none'
  if (isPdfFileName(fileName)) return 'pdf'
  if (isRasterFileName(fileName)) return 'image'
  if (isOfficeFileName(fileName)) return 'office'
  return 'placeholder'
}
